import { FormControl, FormControlLabel, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Checkbox,
  CustomSelect,
  Dialog,
  DialogActions,
  DialogContent,
  Progress,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { MutationStatus } from 'react-query';
import DialogTitle from '@mui/material/DialogTitle';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { configurationTypeData } from '../modules/admin/logic/dropDownOption';
import {
  ExamSaveSubjectRegistrationDraftRequest,
  ExamSeriesModal,
  UpdateSubjectRegistrationDraftRequest,
  ViewSelectedSubjectPapers,
  ViewSubjectDetailResponse,
} from '../api/candidate/exam/model';
import ErrorModal from './modals/ErrorModal';
import {ConfigurationType} from "../api/candidate/config/model";

interface IActionHandling {
  onChangeSetExamSeries: (event: any) => void;
  updateSubject: () => void;
}

interface IEditChooseDialogModal {
  subjectUuid: string;
  subjectName: string;
  subjectCode: string;
  examSeries: ExamSeriesModal[];
  mandatoryPapers: ViewSelectedSubjectPapers[] | [];
  optionalPapers: ViewSelectedSubjectPapers[] | [];
}

interface IPaperStateHandling {
  paperState: any;
  setPaperState: React.Dispatch<React.SetStateAction<any>>;
}

interface IPaperSubjectModal {
  mutateSubjectHandler: IMutateSubjectHandler;
  modelOpen: boolean;
  onClose: (subjectUuid: string) => void;
  actionHandling: IActionHandling;
  modalData: IEditChooseDialogModal;
  classes: any;
  examSeriesUuid?: string;
  isEdit: boolean;
  paperStateHandling: IPaperStateHandling;
}

interface IMutateSubjectHandler {
  mutateSubjectStatus: MutationStatus;
  mutateSubjectReset?: () => void;
}

interface IGetRegisteredSubjectHandler {
  registeredSubjectStatus: MutationStatus;
  registeredSubject: any;
}

interface BaseActionPaperSubjectModal {
  // eslint-disable-next-line react/no-unused-prop-types
  applicationUuid: string;
  mutateSubjectHandler: IMutateSubjectHandler;
  mutateChoosePaperSubject: (payload: any) => void;
  modelOpen: boolean;
  onClose: (subjectUuid: string) => void;
}

export interface ChoosePaperSubjectModalProps extends BaseActionPaperSubjectModal {
  modalData: ViewSubjectDetailResponse;
}

export interface EditPaperSubjectModalProps extends BaseActionPaperSubjectModal {
  paperStateHandling: IPaperStateHandling;
  registeredSubjectHandling: IGetRegisteredSubjectHandler;
}

const useStyles = makeStyles()((theme) =>
({
  chooseEditDialog: {
    '& .MuiPaper-root.MuiDialog-paper': {
      padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
      '& .MuiDialogTitle-root': {
        padding: 0,
      },
      '& .MuiDialogContent-root': {
        padding: 0,
      },
      '& .MuiDialogActions-root': {
        padding: 0,
      },
    },
  },
  formContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  titleContainer: {
    marginBottom: theme.spacing(3),
  },
  textContainer: {
    color: '#96A1AB',
    width: '200px',
    marginRight: theme.spacing(4),
  },
  mandatoryContainer: {
    display: 'flex',
    alignSelf: 'flex-start',
    marginBottom: theme.spacing(3),
  },
  optionalPaperContainer: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  rightContainer: {
    width: 'calc(100% - 180px);',
  },
  optionalPaper: {
    display: 'flex',
    alignItems: 'center',
  },
  progressContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

const PaperSubjectModal = ({
  modelOpen,
  onClose,
  modalData,
  actionHandling,
  mutateSubjectHandler,
  paperStateHandling,
  classes,
  isEdit,
  examSeriesUuid,
}: IPaperSubjectModal) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutateSubjectStatus } = mutateSubjectHandler;
  const { onChangeSetExamSeries, updateSubject } = actionHandling;
  const { setPaperState } = paperStateHandling;
  const onSelectPaper = (optionalPaperUuid: string) => {
    setPaperState((paperState: UpdateSubjectRegistrationDraftRequest) => {
      const findPaper = paperState.optionalPapers.find((paper) => paper.uuid === optionalPaperUuid);
      if (findPaper) {
        findPaper.selected = !findPaper.selected;
      }
      return paperState;
    });
  };

  return (
    <>
      <Dialog className={classes.chooseEditDialog} onClose={onClose} open={modelOpen} maxWidth="sm">
        <DialogTitle className={classes.titleContainer}>
          <Typography variant="h4">{`${modalData?.subjectName} (${modalData?.subjectCode})`}</Typography>
        </DialogTitle>
        <DialogContent>
          {modalData && modalData.examSeries?.length > 1 && (
            <Box className={classes.formContainer}>
              <Typography variant="h5" className={classes.textContainer}>
                Exam Series
              </Typography>
              <Box className={classes.rightContainer}>
                {examSeriesUuid && <CustomSelect
                    id="select-exam-series"
                    menuItems={configurationTypeData(modalData.examSeries)}
                    onChange={onChangeSetExamSeries}
                    value={examSeriesUuid}
                    variant="filled"
                />}

              </Box>
            </Box>
          )}

          {modalData && modalData.optionalPapers?.length > 0 && (
            <>
              <Box className={classes.mandatoryContainer}>
                <Typography variant="h5" className={classes.textContainer}>
                  {t('exam->>SelectSubjects.MandatoryPapers')}
                </Typography>
                <Box className={classes.rightContainer}>
                  {modalData.mandatoryPapers.map((paper) => (
                    <Typography variant="body1" key={paper.modeOfAssessment}>
                      {`Paper ${paper.paperNo}: ${paper.modeOfAssessment}`}
                    </Typography>
                  ))}
                </Box>
              </Box>
              <Box className={classes.optionalPaperContainer}>
                <Typography variant="h5" className={classes.textContainer}>
                  {t('exam->>SelectSubjects.OptionalPapers')}
                </Typography>
                <FormControl className={classes.rightContainer}>
                  {modalData.optionalPapers.map((optional, index) => (
                    <Box key={optional.uuid} className={classes.optionalPaper}>
                      <FormControlLabel
                        key={optional.modeOfAssessment}
                        name="optional"
                        control={
                          <Checkbox
                            id={`checkbox-optional-${index}`}
                            defaultChecked={optional.selected}
                          />
                        }
                        label={`Paper ${optional.paperNo}: ${optional.modeOfAssessment}`}
                        onChange={() => onSelectPaper(optional.uuid)}
                      />
                      <Typography
                        style={{
                          color: theme.color.state.stateInfo,
                          textTransform: 'capitalize',
                          marginLeft: theme.spacing(1),
                        }}
                        variant="body2"
                      >
                        {' '}
                        {optional.status?.toLowerCase()}
                      </Typography>
                    </Box>
                  ))}
                </FormControl>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="text"
            size="medium"
            color="secondary"
            onClick={() => onClose(modalData.subjectCode)}
            id="button-dialog-cancel-papers"
          >
            Cancel
          </Button>
          <ApiButton
            status={mutateSubjectStatus}
            type="button"
            color="secondary"
            size="medium"
            onClick={updateSubject}
            id="button-add-or-update-subject"
          >
            <IconWithLabel
              iconName={isEdit ? IconName.update : IconName.add}
              iconSize="S"
              label={isEdit ? 'Update Subject' : 'Add Subject'}
              typographyProps={{
                variant: 'h5',
                color: 'inherit',
              }}
            />
          </ApiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ChoosePaperSubjectModal = ({
  applicationUuid,
  mutateChoosePaperSubject,
  modelOpen,
  onClose,
  modalData,
  mutateSubjectHandler,
}: ChoosePaperSubjectModalProps) => {
  const { classes } = useStyles();
  const optionalPaperSubjectState: ExamSaveSubjectRegistrationDraftRequest = {
    applicationUuid,
    subjectCode: modalData.subjectCode,
    examSeriesUuid: modalData.examSeries[0].code,
    mandatoryPapers: modalData.mandatoryPapers.map((mandatoryPaper) => mandatoryPaper.uuid),
    optionalPapers: modalData.optionalPapers.map((optPaper) => ({
      uuid: optPaper.uuid,
      selected: false,
      paperNo: optPaper.paperNo,
    })),
    callbackVariable: {
      subjectCode: modalData.subjectCode,
      subjectName: modalData.subjectName,
      subjectLevel: modalData.subjectLevel,
    },
  };
  const [paperState, setPaperState] = useState(optionalPaperSubjectState);
  useEffect(() => {
    setPaperState(optionalPaperSubjectState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelOpen]);

  const paperStateHandling = { paperState, setPaperState };

  const onChangeSetExamSeries = (event: any) => {
    setPaperState((prevState: ExamSaveSubjectRegistrationDraftRequest) => ({
      ...prevState,
      examSeriesUuid: event.target.value,
    }));
  };

  const updateSubject = () => {
    mutateChoosePaperSubject(paperState);
    onClose(modalData.subjectCode);
  };
  return (
    <PaperSubjectModal
      modelOpen={modelOpen}
      isEdit={false}
      onClose={onClose}
      modalData={modalData}
      paperStateHandling={paperStateHandling}
      classes={classes}
      examSeriesUuid={paperState?.examSeriesUuid}
      mutateSubjectHandler={mutateSubjectHandler}
      actionHandling={{
        onChangeSetExamSeries,
        updateSubject,
      }}
    />
  );
};

const EditPaperSubjectModal = ({
  mutateChoosePaperSubject,
  modelOpen,
  onClose,
  mutateSubjectHandler,
  paperStateHandling,
  registeredSubjectHandling,
}: EditPaperSubjectModalProps) => {
  const { classes } = useStyles();
  const { snackbarHandler } = useSnackbarHandler();
  const { t } = useTranslation();
  const { registeredSubject, registeredSubjectStatus } = registeredSubjectHandling;
  const { paperState, setPaperState } = paperStateHandling;
  const previousExamSeriesUuid = useMemo(
    () =>
      registeredSubject?.examSeries?.find(
        // @ts-ignore
        (examSeries) => examSeries.description === registeredSubject.selectedExamSeries
      )?.code,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modelOpen, registeredSubject]
  );
  const updateSubject = () => {
    if (
      snackbarHandler?.setGlobalSnackbar &&
      paperState.optionalPapers.every(
        (newPaper: { selected: any; uuid: any }) =>
          newPaper.selected ===
          registeredSubject.optionalPapers.find(
            (existPaper: { selected: any; uuid: any }) => existPaper.uuid === newPaper.uuid
          ).selected
      ) &&
      paperState.examSeriesUuid === previousExamSeriesUuid
    ) {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>SubjectSnackbar.NoChange'),
      });
      onClose(registeredSubject.subjectCode);
    } else {
      mutateChoosePaperSubject(paperState);
    }
  };

  const onChangeSetExamSeries = (event: any) => {
    setPaperState((prevState: any) => ({
      ...prevState,
      examSeriesUuid: event.target.value,
    }));
  };

  return (
    <>
      {registeredSubjectStatus === 'success' && registeredSubject && (
        <PaperSubjectModal
          isEdit
          modelOpen={modelOpen}
          onClose={onClose}
          classes={classes}
          paperStateHandling={paperStateHandling}
          // @ts-ignore
          examSeriesUuid={paperState?.examSeriesUuid}
          modalData={registeredSubject}
          mutateSubjectHandler={mutateSubjectHandler}
          actionHandling={{
            onChangeSetExamSeries,
            updateSubject,
          }}
        />
      )}
      {registeredSubjectStatus === 'loading' && (
        <Box className={classes.progressContainer}>
          <Progress color="secondary" />
        </Box>
      )}

      {registeredSubjectStatus === 'error' && <ErrorModal />}
    </>
  );
};

export { ChoosePaperSubjectModal, EditPaperSubjectModal };
